import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Mic, Search, Filter, ExternalLink, AlertTriangle, CheckCircle2,
  User, FolderOpen, Clock, Sparkles, ArrowUpRight, Activity,
} from "lucide-react";

const LINK_STATUS = {
  linked: { label: "Linked", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  suggested_match: { label: "Suggested", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  unlinked: { label: "Unlinked", tone: "bg-slate-100 text-slate-600 border-slate-200" },
  ignored: { label: "Ignored", tone: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
}
function fmtDuration(mins) {
  if (!mins && mins !== 0) return "—";
  const m = Math.round(mins);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), r = m % 60;
  return `${h}h ${r}m`;
}

export default function FathomCallsDashboard() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [linkStatus, setLinkStatus] = useState("all");
  const [salesReps, setSalesReps] = useState([]);
  const [salesRepId, setSalesRepId] = useState("all");
  const [hasActionItems, setHasActionItems] = useState("all");

  useEffect(() => { (async () => {
    try {
      const r = await apiClient.get("/users?role=Sales");
      setSalesReps(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch { /* non-blocking */ }
  })(); }, []);

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (linkStatus !== "all") params.set("link_status", linkStatus);
      if (salesRepId !== "all") params.set("sales_rep_id", salesRepId);
      if (hasActionItems === "yes") params.set("has_action_items", "true");
      if (hasActionItems === "no") params.set("has_action_items", "false");
      const res = await apiClient.get(`/fathom/calls?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error("Failed to load calls");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCalls(); /* eslint-disable-next-line */ }, [linkStatus, salesRepId, hasActionItems]);

  const counts = useMemo(() => {
    const c = { linked: 0, suggested_match: 0, unlinked: 0, ignored: 0 };
    items.forEach(i => { c[i.link_status] = (c[i.link_status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="fathom-calls-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Fathom Intelligence
          </div>
          <h1 className="text-3xl font-light text-slate-900 tracking-tight">Sales Calls</h1>
          <p className="text-sm text-slate-500 mt-1">Every sales conversation, transcribed, structured, and linked to projects.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/fathom/settings" data-testid="fathom-settings-link">
            <Button variant="outline" size="sm">Integration Settings</Button>
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard icon={<Mic className="w-4 h-4" />} label="Total" value={total} />
        <StatCard icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} label="Linked" value={counts.linked} />
        <StatCard icon={<AlertTriangle className="w-4 h-4 text-amber-600" />} label="Suggested" value={counts.suggested_match} />
        <StatCard icon={<FolderOpen className="w-4 h-4 text-slate-500" />} label="Unlinked" value={counts.unlinked} />
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by title, project, or rep..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchCalls(); }}
              className="pl-9"
              data-testid="fathom-search-input"
            />
          </div>
          <Select value={linkStatus} onValueChange={setLinkStatus}>
            <SelectTrigger className="w-[170px]" data-testid="fathom-link-status-filter"><SelectValue placeholder="Link status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All link states</SelectItem>
              <SelectItem value="linked">Linked</SelectItem>
              <SelectItem value="suggested_match">Suggested match</SelectItem>
              <SelectItem value="unlinked">Unlinked</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>
          <Select value={salesRepId} onValueChange={setSalesRepId}>
            <SelectTrigger className="w-[200px]" data-testid="fathom-rep-filter"><SelectValue placeholder="Sales rep" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reps</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {salesReps.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={hasActionItems} onValueChange={setHasActionItems}>
            <SelectTrigger className="w-[160px]" data-testid="fathom-action-filter"><SelectValue placeholder="Action items" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All calls</SelectItem>
              <SelectItem value="yes">Has action items</SelectItem>
              <SelectItem value="no">No action items</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchCalls} data-testid="fathom-apply-filters">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Apply
          </Button>
        </div>
      </Card>

      {/* Calls list */}
      {loading ? (
        <Card className="p-10 text-center text-slate-500">Loading calls...</Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="fathom-empty-state">
          <Mic className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No calls yet. Configure the Fathom webhook in <Link to="/fathom/settings" className="text-blue-600 underline">Integration Settings</Link>, or run a backfill.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="fathom-calls-list">
          {items.map(call => (
            <Link key={call.id} to={`/fathom/calls/${call.id}`} data-testid={`fathom-call-${call.id}`}>
              <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-slate-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[15px] font-semibold text-slate-900 truncate">{call.meeting_title}</h3>
                      <Badge variant="outline" className={`text-[10px] ${LINK_STATUS[call.link_status]?.tone || ""}`}>
                        {LINK_STATUS[call.link_status]?.label || call.link_status}
                      </Badge>
                      {call.risk_flag_count > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                          <AlertTriangle className="w-3 h-3 mr-1" /> {call.risk_flag_count} risk{call.risk_flag_count === 1 ? "" : "s"}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(call.meeting_at)}</span>
                      <span>•</span>
                      <span>{fmtDuration(call.duration_minutes)}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {call.sales_rep_name || <span className="text-amber-600">Unassigned</span>}
                      </span>
                      {call.program_name && (<>
                        <span>•</span>
                        <span className="flex items-center gap-1 text-emerald-700">
                          <FolderOpen className="w-3 h-3" /> {call.program_name}
                        </span>
                      </>)}
                    </div>
                    {call.ai_scope_discussed && call.ai_scope_discussed.length > 0 ? (
                      <div className="space-y-1 mt-2" data-testid={`call-scope-${call.id}`}>
                        <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Scope discussed</div>
                        <ul className="space-y-0.5">
                          {call.ai_scope_discussed.slice(0, 3).map((s, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-sm text-slate-600 line-clamp-1">
                              <span className="w-1 h-1 mt-2 rounded-full bg-slate-400 flex-shrink-0"></span>
                              <span className="truncate">{typeof s === "string" ? s : (s.text || JSON.stringify(s))}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : call.summary_preview && !call.has_ai_extraction ? (
                      <p className="text-sm text-slate-500 line-clamp-2 mt-2 italic">{call.summary_preview}</p>
                    ) : null}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span>{call.action_item_count || 0} action items</span>
                      {call.participant_emails?.length > 0 && <span>{call.participant_emails.length} participants</span>}
                      {call.has_ai_extraction && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200"><Sparkles className="w-3 h-3 mr-1" /> AI extracted</Badge>}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <Card className="p-4 border-slate-200 min-w-0">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-slate-500 mb-1.5 truncate">{icon}<span className="truncate">{label}</span></div>
      <div className="text-2xl font-light text-slate-900 tabular-nums truncate">{value}</div>
    </Card>
  );
}
